import { Injectable } from '@angular/core';
import { HttpContext, HttpEvent, HttpInterceptor, HttpHandler, HttpRequest } from '@angular/common/http';
import { catchError, NEVER, Observable, switchMap, throwError } from 'rxjs';
import { JwtAuthService } from '../../services/jwt-auth.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { AuthTokenService } from '../../services/auth-token.service';
import { getClientTimeOffsetHours } from '../../helpers/formatTime-helper';
import { isAnonymousAuthRequest } from '../../helpers/auth-request-helper';
import { isSessionEndedFailure, refreshFailureCause } from '../../helpers/token-refresh-helper';
import { ACCESS_TOKEN_STORAGE_KEY, REFRESHED_BEFORE_SEND } from '../../store/auth-session';

@Injectable()
export class RequestInterceptor implements HttpInterceptor {
  constructor(
    private authService: JwtAuthService,
    private localStorageService: LocalStorageService,
    private authTokenService: AuthTokenService
  ) {}

  private getToken(): string | null {
    return this.localStorageService.retrieveAndDecrypt(ACCESS_TOKEN_STORAGE_KEY);
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    request = request.clone({ setHeaders: { ClientTimeOffset: String(getClientTimeOffsetHours()) } });

    if (isAnonymousAuthRequest(request.url)) {
      return next.handle(request);
    }

    const token = this.getToken();

    if (this.authTokenService.hasRefreshToken() && this.authTokenService.isStoredTokenExpired()) {
      return this.sendAfterRefresh(request, next, token);
    }
    return next.handle(this.authTokenService.addTokenToRequest(request, token));
  }

  private sendAfterRefresh(request: HttpRequest<unknown>, next: HttpHandler, staleToken: string | null): Observable<HttpEvent<unknown>> {
    const marked = request.clone({ context: new HttpContext().set(REFRESHED_BEFORE_SEND, true) });
    return this.authService.refreshToken(staleToken).pipe(
      catchError((failure: unknown) => (isSessionEndedFailure(failure) ? NEVER : throwError(() => refreshFailureCause(failure)))),
      switchMap(token => next.handle(this.authTokenService.addTokenToRequest(marked, token)))
    );
  }
}
